import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { getPayfac, updatePayfac } from "./model";
import s from "./PayfacDetails.module.scss";
import Spin from "components/Spin/Spin";
import merchantManagement from "routes/merchantManagement";
import PayfacForm from "../components/PayfacForm";
import { PortalContext } from "App";
import InnerTabList from "components/InnerTabList/InnerTabList";
import PayfacDetailsAgents from "./PayfacDetailsAgents";
import PayfacDetailsBankInfoConfig from "./PayfacDetailsBankInfoConfig";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import AccountStatusDialog from "../../components/account-status/AccountStatusDialog";
import StatusWrapper from "components/StatusWrapper/StatusWrapper";
import { normalizeAccountStatusDialogData } from "utils/normalizeAccountStatusDialogData";
import responseCodeMapping from "utils/response_code_mapping.json";
import ButtonMAS from "ButtonMAS/ButtonMAS";

const PAGE_LOCALE_KEY = "page.payfacs.addPayfac.";
const DETAIL_PAGE_LOCALE_KEY = "page.payfacs.payfacDetail.";
const DIALOG_LOCALE_KEY = "page.dialogs.";

const STATUS_INIT_STATE = { status: "" };
const STATUS_REMARKS = {
  ACTIVE: [],
  SUSPENDED: [],
  TERMINATED: [],
  PENDING: [],
};

const FIELD_INIT_STATE = {
  name: "",
  firstName: "",
  lastName: "",
  email: "",
  address: "",
  phone: "",
  remarks: "",
  status: "ACTIVE",
  updated: "",
  contactPerson: "",
  alipayMerchantIdPrefix: "",
  alipayStoreIdPrefix: "",
  alipayPartnerKey: "",
  alipayPartnerId: "",
  wechatMerchantId: "",
  wechatAppId: "",
  wechatSecret: "",
  wechatKey: "",
  yedpayPayfacId: "",
  yedpayKid: "",
  yedpayPayfacHashId: "",
  yedpayPayfacPrefix: "",
  payfacPrefix: "",
};

const normalizePayfacData = (data = {}) => {
  const sanitized = Object.entries(data).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});

  return {
    ...FIELD_INIT_STATE,
    ...sanitized,
  };
};

const formatPayfacLabel = ({ id, payfacPrefix, name } = {}) => {
  return [id, payfacPrefix, name].filter(Boolean).join(" - ");
};

const PayfacDetails = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;
  const [payfacId, setPayfacId] = useState("");

  const [fieldInput, setFieldInput] = useState(FIELD_INIT_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [accountStatusDialogData, setAccountStatusDialogData] = useState(
    STATUS_INIT_STATE,
  );
  const [isAccountStatusDialogOpen, setIsAccountStatusDialogOpen] =
    useState(false);

  const [selectedTab, setSelectedTab] = useState("detail_info");

  useEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    const id =
      detailPageState?.[path]?.id ?? sessionState?.[path]?.id ?? "";
    if (id) {
      setPayfacId(id);
      fetchPayfacDetails(id);
    } else {
      setLoading(false);
    }
  }, [detailPageState[path]]);

  const fetchPayfacDetails = async (id) => {
    setLoading(true);
    const apiResponse = await getPayfac(id);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
      setLoading(false);
      return;
    }

    const responseBody = apiResponse.data;
    if (responseBody.responseCode === "0000") {
      setFieldInput(normalizePayfacData(responseBody.data));
    } else {
      snackbar(responseBody.message || "Failed to fetch details", "error");
    }
    setLoading(false);
  };

  const handleSubmit = async (fieldInput) => {
    setSubmitting(true);
    const payload = { ...fieldInput };
    delete payload.updated;

    const apiResponse = await updatePayfac(payfacId, payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode !== "0000") {
            snackbar(
              t(PAGE_LOCALE_KEY + "create.msgFailed") +
                (responseBody.message || ""),
              "error",
            );
          } else {
            snackbar("Update success", "success");
            const newpath = "/merchant-management/payfacs/payfacs";
            const redirectpayload = {
              name: merchantManagement
                .find((tab) => tab.tabName === "PayFacs")
                .page.find((page) => page.path === newpath).name,
              path: newpath,
              refresh: true,
            };

            redirectFn("close", { tabId: path });
            redirectFn("redirectPage", redirectpayload);
          }
        }
      }
    }
    setSubmitting(false);
  };

  // ===================== status dialog function ===================== //

  const openAccountStatusDialog = (data) => {
    setAccountStatusDialogData(normalizeAccountStatusDialogData(data));
    setIsAccountStatusDialogOpen(true);
  };

  const closeAccountStatusDialog = () => {
    setAccountStatusDialogData(STATUS_INIT_STATE);
    setIsAccountStatusDialogOpen(false);
  };

  const updatePayload = (fieldInput) => {
    setAccountStatusDialogData(fieldInput);
  };

  const updateResultCallback = async () => {
    if (!payfacId) return;
    setStatusSubmitting(true);

    const apiResponse = await updatePayfac(payfacId, {
      status: accountStatusDialogData.status,
    });
      if (!apiResponse || !apiResponse?.data) {
        snackbar(t("general.message.networkError"), "error");
      } else {
        switch (apiResponse.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            setStatusSubmitting(false);
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            const responseBody = apiResponse.data;
            if (responseBody.responseCode !== "0000") {
              snackbar(
                t(DIALOG_LOCALE_KEY + "changeStatus.msgFailed") +
                  responseCodeMapping[responseBody.responseCode ?? "error"][
                    "en"
                  ],
                "error",
              );
            } else {
              setFieldInput((state) => ({
                ...state,
                status: accountStatusDialogData.status,
              }));
              snackbar(
                t(
                  DIALOG_LOCALE_KEY +
                    "changeStatus.msgSuccess." +
                    accountStatusDialogData.status.toLowerCase(),
                ),
                "success",
              );
            }
          }
        }
      }
    setStatusSubmitting(false);
    closeAccountStatusDialog();
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const tabList = [
    {
      key: "detail_info",
      label: t(DETAIL_PAGE_LOCALE_KEY + "detailTitle"),
      value: "detail_info",
    },
    {
      key: "detail_agents",
      label: t(DETAIL_PAGE_LOCALE_KEY + "sectionAgentsTitle"),
      value: "detail_agents",
    },
    {
      key: "detail_bank_info_config",
      label: t(DETAIL_PAGE_LOCALE_KEY + "sectionBankInfoConfigTitle"),
      value: "detail_bank_info_config",
    },
  ];

  const renderContent = () => {
    switch (selectedTab) {
      case "detail_info":
        return (
          <div className={s.contentContainer}>
            {loading ? <Spin /> : <></>}
            <div className={loading ? s.loadingNow : ""}>
              <PayfacForm
                initialValues={fieldInput}
                onSubmit={handleSubmit}
                submitting={submitting}
                loading={loading}
                isEditMode={true}
                showActionDivider={false}
                gridId="PayfacDetailsGrid"
              />
            </div>
          </div>
        );
      case "detail_agents":
        return (
          <PayfacDetailsAgents
            payfacId={payfacId}
            payfacPrefix={fieldInput.payfacPrefix}
            redirectFn={redirectFn}
          />
        );
      case "detail_bank_info_config":
        return <PayfacDetailsBankInfoConfig payfacId={payfacId} />;
      default:
        return null;
    }
  };

  const renderHeader = () => {
    return (
      <div className={s.agentDetails}>
        <div className={s.detailItemContainer}>
          <div className={s.titleContent}>
            <div className={s.itemContainer}>
              <span>
                {formatPayfacLabel({
                  id: payfacId,
                  payfacPrefix: fieldInput.payfacPrefix,
                  name: fieldInput.name,
                }) || `${payfacId || "-"} - -`}
              </span>
            </div>
            <div className={s.dialogActionContainer}>
              <StatusWrapper status={fieldInput.status || "-"} />

              <div className={s.dialogButtonContainer}>
                <ButtonMAS
                  overlayLoading
                  disabled={submitting || statusSubmitting}
                  onClick={() => {
                    return openAccountStatusDialog(fieldInput);
                  }}
                >
                  {t("common.edit")}
                </ButtonMAS>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={s.container}>
      {renderHeader()}
      <div className={s.innerTabContainer}>
        <InnerTabList
          selectedTab={selectedTab}
          handleChangeTab={(event, innerTabName) => setSelectedTab(innerTabName)}
          tabList={tabList}
        />
      </div>
      {renderContent()}
      <SimpleDialog
        open={isAccountStatusDialogOpen}
        dialogOncloseFn={closeAccountStatusDialog}
        title={t(DIALOG_LOCALE_KEY + "changeStatus.title")}
        content={
          <AccountStatusDialog
            data={accountStatusDialogData}
            remarksList={STATUS_REMARKS}
            updatePayload={updatePayload}
          />
        }
        positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
        negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
        confirmFn={!statusSubmitting ? updateResultCallback : () => {}}
        positiveActionLoading={statusSubmitting}
        negativeActionLoading={statusSubmitting}
      />
    </div>
  );
};

export default React.memo(PayfacDetails);
